import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Bar, Line } from 'react-chartjs-2';
import {
  buildCombinedOperationalSlots,
  formatAllLocationsHoursLabel,
  getOperationalSlotIdFromTimestamp,
} from '../../utils/locationHours';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

const MCALLEN_LABEL = 'McAllen EPL';
const BROWNSVILLE_LABEL = 'Brownsville EPL';
const MCALLEN_COLOR = 'rgba(54, 162, 235, 1)';
const BROWNSVILLE_COLOR = 'rgba(245, 158, 11, 1)';
const MCALLEN_LIGHT = 'rgba(54, 162, 235, 0.2)';
const BROWNSVILLE_LIGHT = 'rgba(245, 158, 11, 0.2)';

const COMBINED_OPERATIONAL_SLOTS = buildCombinedOperationalSlots();

export const buildDayWiseChartData = (dayData, parseTimestamp, isTournamentGame, isExcludedFromDashboardStats) => {
  if (!dayData?.analysisData) return null;

  const { mcallen, brownsville } = dayData.analysisData;
  const mcallenTimeData = {};
  const brownsvilleTimeData = {};
  const businessDayKey = dayData.date;

  mcallen.labels.forEach((timestamp, index) => {
    if (isTournamentGame(mcallen.game_name[index])) return;
    if (isExcludedFromDashboardStats(mcallen.game_name[index])) return;
    const slotId = getOperationalSlotIdFromTimestamp(timestamp, businessDayKey);
    if (!slotId) return;
    mcallenTimeData[slotId] = (mcallenTimeData[slotId] || 0) + mcallen.tables[index];
  });

  brownsville.labels.forEach((timestamp, index) => {
    if (isTournamentGame(brownsville.game_name[index])) return;
    if (isExcludedFromDashboardStats(brownsville.game_name[index])) return;
    const slotId = getOperationalSlotIdFromTimestamp(timestamp, businessDayKey);
    if (!slotId) return;
    brownsvilleTimeData[slotId] = (brownsvilleTimeData[slotId] || 0) + brownsville.tables[index];
  });

  return {
    labels: COMBINED_OPERATIONAL_SLOTS.map((slot) => slot.label),
    datasets: [
      {
        label: MCALLEN_LABEL,
        data: COMBINED_OPERATIONAL_SLOTS.map((slot) => mcallenTimeData[slot.id] || 0),
        borderColor: MCALLEN_COLOR,
        backgroundColor: MCALLEN_LIGHT,
        borderWidth: 2,
        fill: true,
        tension: 0.3,
        pointRadius: 0,
      },
      {
        label: BROWNSVILLE_LABEL,
        data: COMBINED_OPERATIONAL_SLOTS.map((slot) => brownsvilleTimeData[slot.id] || 0),
        borderColor: BROWNSVILLE_COLOR,
        backgroundColor: BROWNSVILLE_LIGHT,
        borderWidth: 2,
        fill: true,
        tension: 0.3,
        pointRadius: 0,
      },
    ],
  };
};

export const computeDaySummary = (analysisData, isTournamentGame, isExcludedFromDashboardStats) => {
  if (!analysisData) return { mcallen: 0, brownsville: 0 };

  const sumSide = (source) => {
    let total = 0;
    let tournamentTotal = 0;
    source.game_name.forEach((game, index) => {
      if (isExcludedFromDashboardStats(game)) return;
      const count = source.tables[index];
      total += count;
      if (isTournamentGame(game)) tournamentTotal += count;
    });
    return total - tournamentTotal;
  };

  return {
    mcallen: sumSide(analysisData.mcallen),
    brownsville: sumSide(analysisData.brownsville),
  };
};

const getDayWiseChartOptions = (title, { compact = true } = {}) => ({
  responsive: true,
  maintainAspectRatio: false,
  animation: false,
  transitions: { active: { animation: { duration: 0 } } },
  interaction: { intersect: false, mode: 'index' },
  plugins: {
    title: {
      display: Boolean(title),
      text: title,
      font: { size: compact ? 11 : 16, weight: 'bold' },
      padding: compact ? 6 : 12,
    },
    legend: {
      display: true,
      position: 'top',
      labels: {
        usePointStyle: true,
        padding: compact ? 6 : 14,
        font: { size: compact ? 9 : 13 },
      },
    },
    tooltip: {
      callbacks: {
        label: (context) => `${context.dataset.label}: ${context.parsed.y} tables`,
      },
    },
  },
  scales: {
    x: {
      ticks: {
        font: { size: compact ? 6 : 10 },
        maxRotation: 45,
        minRotation: 45,
        autoSkip: !compact,
        maxTicksLimit: compact ? 16 : undefined,
      },
      grid: { display: !compact, color: 'rgba(0, 0, 0, 0.08)' },
    },
    y: {
      beginAtZero: true,
      ticks: {
        font: { size: compact ? 8 : 12 },
        stepSize: 1,
        precision: 0,
        callback: (value) => (Number.isInteger(value) ? value : undefined),
      },
      grid: { color: 'rgba(0, 0, 0, 0.06)' },
      title: {
        display: true,
        text: 'Tables',
        font: { size: compact ? 9 : 13, weight: 'bold' },
      },
    },
  },
});

const ChartSkeleton = () => (
  <div className="flex h-full flex-col justify-center gap-3 px-2">
    <div className="flex items-end justify-between gap-1 h-[140px]">
      {[35, 55, 40, 70, 45, 85, 50, 65, 42, 78].map((h, i) => (
        <div
          key={i}
          className="flex-1 rounded-t-md bg-gradient-to-t from-slate-200 to-slate-100 animate-pulse"
          style={{ height: `${h}%`, animationDelay: `${i * 80}ms` }}
        />
      ))}
    </div>
    <div className="mx-auto h-3 w-24 rounded-full bg-slate-200/80 animate-pulse" />
  </div>
);

const DayDetailModal = ({
  open,
  onClose,
  dayData,
  summary,
  chartType,
  parseTimestamp,
  isTournamentGame,
  isExcludedFromDashboardStats,
}) => {
  const chartData = useMemo(() => {
    if (!dayData) return null;
    return buildDayWiseChartData(dayData, parseTimestamp, isTournamentGame, isExcludedFromDashboardStats);
  }, [dayData, parseTimestamp, isTournamentGame, isExcludedFromDashboardStats]);

  const chartOptions = useMemo(
    () => getDayWiseChartOptions(`Table Count by Time · ${dayData?.displayDate || ''}`, { compact: false }),
    [dayData?.displayDate]
  );

  const ChartComponent = chartType === 'line' ? Line : Bar;
  const diff = (summary?.mcallen || 0) - (summary?.brownsville || 0);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-h-[95vh] w-[95vw] max-w-6xl overflow-y-auto bg-white p-4 sm:p-6">
        <DialogHeader className="pr-8">
          <DialogTitle className="text-xl font-bold text-slate-900 sm:text-2xl">
            {dayData?.displayDate || 'Day Details'}
          </DialogTitle>
          <p className="text-sm text-slate-500">
            Full operational day chart · {formatAllLocationsHoursLabel()}
          </p>
        </DialogHeader>

        {summary && (
          <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-3">
            <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-center">
              <div className="text-sm font-medium text-blue-600">{MCALLEN_LABEL}</div>
              <div className="mt-1 text-2xl font-bold text-blue-700">{summary.mcallen}</div>
            </div>
            <div className="rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 text-center">
              <div className="text-sm font-medium text-amber-600">{BROWNSVILLE_LABEL}</div>
              <div className="mt-1 text-2xl font-bold text-amber-700">{summary.brownsville}</div>
            </div>
            <div className="col-span-2 rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-center sm:col-span-1">
              <div className="text-sm font-medium text-emerald-600">Difference</div>
              <div className="mt-1 text-2xl font-bold text-emerald-700">
                {diff > 0 ? '+' : ''}
                {diff}
              </div>
            </div>
          </div>
        )}

        <div className="mt-4 h-[380px] rounded-xl border border-slate-200 bg-slate-50/60 p-3 sm:h-[480px] sm:p-4">
          {dayData?.error ? (
            <div className="flex h-full items-center justify-center text-red-500">{dayData.error}</div>
          ) : chartData ? (
            <ChartComponent options={chartOptions} data={chartData} />
          ) : (
            <div className="flex h-full items-center justify-center text-slate-500">No data available</div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const DayWiseChartCard = React.memo(function DayWiseChartCard({
  dayData,
  summary,
  chartType,
  parseTimestamp,
  isTournamentGame,
  isExcludedFromDashboardStats,
  onOpen,
}) {
  const containerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;

    let idleId = null;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          idleId = window.setTimeout(() => setIsVisible(true), 40);
        } else {
          if (idleId) window.clearTimeout(idleId);
          setIsVisible(false);
        }
      },
      { rootMargin: '80px 0px', threshold: 0.01 }
    );

    observer.observe(node);
    return () => {
      if (idleId) window.clearTimeout(idleId);
      observer.disconnect();
    };
  }, []);

  const chartData = useMemo(() => {
    if (!isVisible) return null;
    return buildDayWiseChartData(dayData, parseTimestamp, isTournamentGame, isExcludedFromDashboardStats);
  }, [isVisible, dayData, parseTimestamp, isTournamentGame, isExcludedFromDashboardStats]);

  const chartOptions = useMemo(
    () => getDayWiseChartOptions(dayData.displayDate, { compact: true }),
    [dayData.displayDate]
  );

  const ChartComponent = chartType === 'line' ? Line : Bar;

  return (
    <div
      ref={containerRef}
      role="button"
      tabIndex={0}
      onClick={() => onOpen?.(dayData)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen?.(dayData);
        }
      }}
      className="w-full cursor-pointer rounded-xl border border-slate-200/80 bg-white p-4 text-left shadow-sm transition hover:border-blue-300 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-400"
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-base font-semibold text-slate-800">{dayData.displayDate}</h3>
        <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-600">
          View full
        </span>
      </div>

      <div className="h-[220px] sm:h-56">
        {dayData.error ? (
          <div className="flex h-full items-center justify-center text-sm text-red-500">{dayData.error}</div>
        ) : isVisible && chartData ? (
          <ChartComponent options={chartOptions} data={chartData} />
        ) : (
          <ChartSkeleton />
        )}
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
        <div className="rounded-lg bg-blue-50/80 px-2 py-2 text-center ring-1 ring-blue-100">
          <div className="font-semibold text-blue-600">{MCALLEN_LABEL}</div>
          <div className="text-lg font-bold text-blue-700">{summary.mcallen}</div>
        </div>
        <div className="rounded-lg bg-amber-50/80 px-2 py-2 text-center ring-1 ring-amber-100">
          <div className="font-semibold text-amber-600">{BROWNSVILLE_LABEL}</div>
          <div className="text-lg font-bold text-amber-700">{summary.brownsville}</div>
        </div>
      </div>
    </div>
  );
});

export const DayWiseProcessingPanel = () => (
  <div className="flex h-48 items-center justify-center">
    <div className="h-10 w-10 animate-spin rounded-full border-2 border-slate-200 border-t-blue-600" />
  </div>
);

const DayWiseBreakdown = ({
  entries,
  loading,
  chartType,
  parseTimestamp,
  isTournamentGame,
  isExcludedFromDashboardStats,
}) => {
  const [selectedDay, setSelectedDay] = useState(null);

  const entriesWithSummary = useMemo(
    () =>
      entries.map((entry) => ({
        ...entry,
        summary: computeDaySummary(entry.analysisData, isTournamentGame, isExcludedFromDashboardStats),
      })),
    [entries, isTournamentGame, isExcludedFromDashboardStats]
  );

  if (loading) {
    return <DayWiseProcessingPanel />;
  }

  if (entriesWithSummary.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-slate-200 py-12 text-center text-slate-500">
        No day-wise data available for the selected range
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {entriesWithSummary.map((dayData) => (
          <DayWiseChartCard
            key={dayData.date}
            dayData={dayData}
            summary={dayData.summary}
            chartType={chartType}
            parseTimestamp={parseTimestamp}
            isTournamentGame={isTournamentGame}
            isExcludedFromDashboardStats={isExcludedFromDashboardStats}
            onOpen={setSelectedDay}
          />
        ))}
      </div>

      <DayDetailModal
        open={Boolean(selectedDay)}
        onClose={() => setSelectedDay(null)}
        dayData={selectedDay}
        summary={selectedDay?.summary}
        chartType={chartType}
        parseTimestamp={parseTimestamp}
        isTournamentGame={isTournamentGame}
        isExcludedFromDashboardStats={isExcludedFromDashboardStats}
      />
    </>
  );
};

export default DayWiseBreakdown;
